import React from 'react';
import { useParams } from 'react-router-dom';
import { Text } from '@perch-labs/shrike';
import { timeSince } from '@perch-labs/warbler';
import SVG from 'react-inlinesvg';
import AcidRainIcon from '/images/icons/acid-rain.svg';
import CarIcon from '/images/icons/car.svg';
import Co2Icon from '/images/icons/co2.svg';
import CoalIcon from '/images/energy-sources/coal.svg';
import FactoryIcon from '/images/icons/factory.svg';
import Fishcon from '/images/icons/fish.svg';
import FlareIcon from '/images/icons/flare.svg';
import GasesIcon from '/images/icons/gases.svg';
import NaturalGas from '/images/energy-sources/natural-gas.svg';
import NuclearIcon from '/images/energy-sources/nuclear.svg';
import OilIcon from '/images/energy-sources/oil.svg';
import RadioactiveIcon from '/images/icons/radioactive.svg';
import RigIcon from '/images/icons/rig.svg';
import { copyFor, routes } from 'config';
import { BackLink } from 'components/back-link';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { Hr, Page, Sources, SourceText } from 'styles';
import { MultipartDescription } from 'components/multipart-description';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { useGridStats } from 'hooks/use-grid-stats.hook';
import { useImpactStats } from 'hooks/use-impact-stats.hook';
import { monthString, roundWithCommas } from 'utils/format';
import { Container } from 'pages/usage/usage.style';
import { getStats } from './energy-details.utils';

const iconMap = {
  coal: { info: [FactoryIcon, AcidRainIcon, Fishcon], source: CoalIcon },
  gas: { info: [Co2Icon, RigIcon, FlareIcon], source: NaturalGas },
  nuclear: {
    info: [GasesIcon, FactoryIcon, RadioactiveIcon],
    source: NuclearIcon,
  },
  oil: { info: [FactoryIcon, CarIcon, Fishcon], source: OilIcon },
};

interface Props {
  location?: any;
}

const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

export const EnergyDetails = (props: Props) => {
  const { planId, source } = useParams<{
    planId?: string;
    source: 'coal' | 'gas' | 'nuclear' | 'oil';
  }>();
  const { usage } = routes({ planId });

  const previousAppLocation = props.location.state.from;
  const getCopy = copyFor(`usage.energyDetails.${source}`);
  const {
    data: gridStats,
    loading: gridLoading,
    error: gridError,
  } = useGridStats();
  const {
    data: impactStats,
    loading: impactLoading,
    error: impactError,
  } = useImpactStats({ planId: Number(planId) });

  if (gridLoading || impactLoading) return <LoadingPage />;
  if (gridError || impactError || !gridStats || !impactStats) {
    return (
      <ErrorPage title={getCopy('title')} to={previousAppLocation || usage} />
    );
  }

  const icons = iconMap[source];

  const { equivalency, percent, units } = getStats(
    impactStats.fuelTypeEquivalency,
    gridStats.energyBreakdown,
    source
  );

  const parts = getCopy('facts').map((fact: string, index: number) => ({
    description: <Text textStyle="heading500">{fact}</Text>,
    icon: icons.info[index],
    id: fact.slice(0, 10),
  }));

  const paragraphs = getCopy('paragraphs').map((paragraph: string) => (
    <Text
      key={paragraph.slice(10, 20)}
      margin={{ bottom: '16px' }}
      opacity="medium"
    >
      {paragraph}
    </Text>
  ));

  const location =
    previousAppLocation === usage
      ? getBackLinkCopy('usage')
      : getBackLinkCopy('energyAnalysis');

  return (
    <>
      <BackLink to={previousAppLocation || usage} location={location} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1">
          {getCopy('title')}
        </Text>
        <Text margin={{ bottom: '32px', top: '16px' }} opacity="medium">
          {getCopy('paragraph1', {
            equivalency: roundWithCommas(equivalency),
            months: monthString(
              timeSince(impactStats.createdAt || '', {
                units: 'months',
              }) as number
            ),
            percent,
            units,
          })}
        </Text>
        <Container flex justifyContent="space-around">
          <Container
            flex
            direction="column"
            justifyContent="center"
            width="45%"
          >
            <Text textStyle="heading900">
              {getCopy('total.amount', {
                equivalency: roundWithCommas(equivalency),
              })}
            </Text>
            <Text opacity="medium">{getCopy('total.label', { units })}</Text>
          </Container>
          <SVG
            height="88px"
            width="88px"
            src={icons.source}
            aria-hidden="true"
          />
        </Container>
        <Text margin={{ top: '32px' }} opacity="medium">
          {getCopy('paragraph2')}
        </Text>
        <MultipartDescription
          parts={parts}
          margin={{ bottom: '32px', top: '32px' }}
        />
        {paragraphs}
        <Hr />
        <Sources>
          {getCopy('sources').map((item: string) => (
            <SourceText key={item}>{item}</SourceText>
          ))}
        </Sources>
      </Page>
    </>
  );
};
